import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { apiErrorMessage } from "@/lib/api";
import EkgLine from "@/components/EkgLine";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from || "/";

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const user = await login(email.trim(), password);
            // Jeff-first (Todd, 2026-09-23): land admins on the agent chat
            const role = user?.role;
            const isCrew = role && role !== "admin" && role !== "supervisor";
            navigate(from !== "/" ? from : isCrew ? "/" : "/agent", { replace: true });
        } catch (err) {
            setError(apiErrorMessage(err, "Login failed"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left visual rail */}
            <div
                className="hidden lg:flex w-[42%] relative flex-col justify-between p-12 text-white"
                style={{ background: "var(--brand-dark)" }}
            >
                <EkgLine className="absolute top-0 left-0 right-0" />
                <div className="relative z-10">
                    <div className="logo-plate" style={{ width: "fit-content" }}>
                        <img
                            src="/brand/logo-512.png"
                            alt="CPR Paint & Coatings"
                            style={{ height: 56, display: "block" }}
                        />
                    </div>
                </div>
                <div className="relative z-10 fade-up">
                    <p className="eyebrow" style={{ color: "#d6d2c9" }}>
                        Admin Control Center
                    </p>
                    <h1
                        className="font-display"
                        style={{
                            fontSize: "clamp(2rem,3.5vw,2.75rem)",
                            fontWeight: 800,
                            letterSpacing: "-0.02em",
                            lineHeight: 1.05,
                            marginTop: "1.25rem",
                            color: "#fff",
                        }}
                    >
                        Run the field, <span style={{ color: "var(--brand-red)" }}>not the paperwork</span>.
                    </h1>
                    <p
                        style={{
                            marginTop: "1.25rem",
                            maxWidth: 420,
                            color: "#d6d2c9",
                            lineHeight: 1.6,
                        }}
                    >
                        Estimates, jobs, leads from the website, crew onboarding, and
                        training compliance — all in one place. Built for the team that
                        brings spaces back to life.
                    </p>
                </div>
                <EkgLine className="absolute bottom-0 left-0 right-0" />
            </div>

            {/* Right form */}
            <div className="flex-1 flex items-center justify-center px-5 py-12">
                <div className="w-full max-w-[420px] fade-up" data-testid="login-card">
                    <div className="lg:hidden mb-8 flex justify-center">
                        <div className="logo-plate">
                            <img
                                src="/brand/logo-512.png"
                                alt="CPR Paint"
                                style={{ height: 48 }}
                            />
                        </div>
                    </div>
                    <p className="eyebrow">Sign in</p>
                    <h2 className="h1 mt-3">Welcome back.</h2>
                    <p className="text-muted mt-3">
                        Enter your email and password to access the admin platform.
                    </p>

                    <form className="mt-8 flex flex-col gap-5" onSubmit={onSubmit}>
                        <div>
                            <label className="field-label" htmlFor="email">
                                Email address
                            </label>
                            <input
                                id="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="field-input"
                                placeholder="you@cprpainting.com"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <div className="flex items-baseline justify-between">
                                <label className="field-label" htmlFor="password">
                                    Password
                                </label>
                                <Link
                                    to="/forgot-password"
                                    className="link-red text-sm"
                                    data-testid="login-forgot-link"
                                >
                                    Forgot?
                                </Link>
                            </div>
                            <input
                                id="password"
                                type="password"
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="field-input"
                                placeholder="••••••••"
                                data-testid="login-password-input"
                            />
                        </div>

                        {error && (
                            <div
                                className="field-error"
                                role="alert"
                                data-testid="login-error"
                                style={{
                                    background: "rgba(210,5,28,0.08)",
                                    padding: "0.75rem 1rem",
                                    borderRadius: 8,
                                    marginTop: "-0.25rem",
                                }}
                            >
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            className="btn btn-primary w-full"
                            disabled={loading}
                            data-testid="login-submit-btn"
                        >
                            {loading ? "Signing in…" : "Sign in"}
                            {!loading && <ArrowRight size={16} />}
                        </button>
                    </form>

                    <div
                        className="mt-10 flex items-center gap-2 text-xs text-muted"
                        style={{ borderTop: "1px solid var(--brand-surface)", paddingTop: "1rem" }}
                    >
                        <Lock size={14} />
                        Secure session — your password is hashed with bcrypt and never stored in plain text.
                    </div>
                </div>
            </div>
        </div>
    );
}
